import { FormTemplate } from '../services/formTemplateService';
import { formatFullName } from './nameFormatter';

/**
 * System fields that are required for booking creation
 */
export interface BookingSystemFields {
  name: string; // Combined from firstName + lastName
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  notes?: string;
  company?: string;
  companySize?: string;
}

/**
 * Extract system field values from form data using the form template
 *
 * This function dynamically extracts required booking fields (name, email, etc.)
 * from the form data by looking up which fields have systemFieldKey properties.
 * This makes the system flexible to work with ANY custom form structure.
 *
 * @param formData - The form data object with field IDs as keys
 * @param formTemplate - The form template containing field definitions
 * @returns Object with extracted system field values
 *
 * @example
 * // Form has field with id="abc-123" and systemFieldKey="name"
 * const formData = { "abc-123": "John Doe", "def-456": "john@example.com" };
 * const extracted = extractSystemFields(formData, template);
 * // Returns: { name: "John Doe", email: "john@example.com", ... }
 */
export function extractSystemFields(
  formData: Record<string, any>,
  formTemplate: FormTemplate | null
): BookingSystemFields {
  const systemFields: BookingSystemFields = {
    name: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: '',
    company: '',
    companySize: ''
  };

  // If no template, return empty fields
  if (!formTemplate || !formTemplate.fields) {
    return systemFields;
  }

  // Map form field IDs to system field keys
  formTemplate.fields.forEach(field => {
    // Only process fields that are system fields
    if (field.isSystemField && field.systemFieldKey) {
      const fieldValue = formData[field.id];

      // Store the value if it exists
      if (fieldValue !== undefined && fieldValue !== null) {
        // Handle different field types
        if (Array.isArray(fieldValue)) {
          // For checkbox/multiselect fields, join values
          systemFields[field.systemFieldKey as keyof BookingSystemFields] = fieldValue.join(', ');
        } else {
          // For all other fields, store as string
          systemFields[field.systemFieldKey as keyof BookingSystemFields] = String(fieldValue);
        }
      }
    }
  });

  // Combine firstName and lastName into name field for backward compatibility
  // Uses formatFullName to handle Japanese name ordering (lastName firstName)
  if (systemFields.firstName || systemFields.lastName) {
    systemFields.name = formatFullName(systemFields.firstName, systemFields.lastName);
  }

  return systemFields;
}

/**
 * Validate that required system fields have values
 *
 * @param formData - The form data object
 * @param formTemplate - The form template
 * @returns Object with isValid flag and array of missing field labels
 */
export function validateRequiredSystemFields(
  formData: Record<string, any>,
  formTemplate: FormTemplate | null
): { isValid: boolean; missingFields: string[] } {
  const missingFields: string[] = [];

  if (!formTemplate || !formTemplate.fields) {
    return { isValid: false, missingFields: ['Form template not loaded'] };
  }

  // Check for required system fields
  const requiredSystemFields = ['firstName', 'lastName', 'email'];

  formTemplate.fields.forEach(field => {
    if (
      field.isSystemField &&
      field.systemFieldKey &&
      requiredSystemFields.includes(field.systemFieldKey) &&
      field.required
    ) {
      const value = formData[field.id];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        missingFields.push(field.label);
      }
    }
  });

  return {
    isValid: missingFields.length === 0,
    missingFields
  };
}

/**
 * Get the field ID for a specific system field key
 *
 * @param formTemplate - The form template
 * @param systemFieldKey - The system field key to look up (e.g., 'name', 'email')
 * @returns The field ID if found, undefined otherwise
 */
export function getFieldIdBySystemKey(
  formTemplate: FormTemplate | null,
  systemFieldKey: string
): string | undefined {
  if (!formTemplate || !formTemplate.fields) {
    return undefined;
  }

  const field = formTemplate.fields.find(
    f => f.isSystemField && f.systemFieldKey === systemFieldKey
  );

  return field?.id;
}
